import {
  AutoFixHigh,
  CameraAlt,
  Garage,
  CarRepair,
  Flare,
  AutoAwesomeMotion,
  LocalCarWash,
  Brush,
  PublishedWithChanges,
  FormatPaint,
  AutoAwesome,
  Album,
  ManageSearch,
  BlurCircular,
  EventSeat,
  PhotoFilter,
  Send,
  MoreVert,
} from '@mui/icons-material';
import {
  Grid,
  IconButton,
  Stack,
  Icon,
  Box,
  Avatar,
  Tooltip,
} from '@mui/material';
import Card from '@mui/material/Card';
import { createTheme, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderTypes, ProcessStep } from '../../common/enums/enums';
import { differenceDateTime } from '../../helpers/date';
import { getShortOrderNumber, getWOPrefix } from '../../helpers/orders';
import { isReconditioning } from '../../helpers/reconditioning';
import { GenCurrency, GenDate } from '../GenDataFormat';
import {
  PendingApprovalIcon,
  PauseIcon,
  ApprovedIcon,
  InTransitIcon,
  InProcessIcon,
  DoneIcon,
  DeclinedIcon,
  ArrivedIcon,
} from '../Icons/Services';
import { formatTextServices, uniqueServicesOnly } from './WOrderTag';
import WOrderTagMenu from './WOrderTagMenu';

const theme = createTheme({
  palette: {
    done: {
      footerText: '#4CAF50',
      background: '#4CAF50',
    },
    pending_approval: {
      footerText: '#EB8C00',
      background: '#E6E0E9',
      header_text_color: '#000',
      vertical_icon_color: '#0000004F',
    },
    canceled: {
      footerText: '#F4433680',
      background: '#F4433680',
      vertical_icon_color: '#FFFFFFB5',
    },
    declined: {
      footerText: '#FF3E3E',
      background: '#FF3E3E',
      vertical_icon_color: '#FFFFFFB5',
    },
    approved: {
      footerText: '#2AD231',
      background: '#90EE90',
      header_text_color: '#000',
      vertical_icon_color: '#000',
    },
    intransit: {
      footerText: '#0597E0',
      background: '#0597E0',
      vertical_icon_color: '#FFF',
    },
    arrived: {
      footerText: '#0057B2',
      background: '#0057B2',
      vertical_icon_color: '#F2F2F2B5',
    },
    inprocess: {
      footerText: '#FF9800',
      background: '#FF9800',
      header_text_color: '#000',
      vertical_icon_color: '#0000004F',
    },
  },
});

const {
  GENERATED,
  APPROVED,
  IN_TRANSIT,
  ARRIVED,
  IN_PROCESS,
  DONE,
  CANCELED,
  TRANSFERRED,
  DECLINED,
  PAUSED,
  PENDING_APPROVAL,
} = ProcessStep;

export const getStatusColors = step => {
  const { palette } = theme;

  switch (step) {
    case APPROVED:
      return {
        header_text_color: palette.approved.header_text_color,
        vertical_icon_color: palette.approved.vertical_icon_color,
        custom_background_color: palette.approved.background,
        footer_text_color: palette.approved.footerText,
        footer_text: ProcessStep.APPROVED,
        footer_status_icon: (
          <ApprovedIcon size="small" color={palette.approved.footerText} />
        ),
      };
    case IN_TRANSIT:
      return {
        header_text_color: palette.primary.contrastText,
        vertical_icon_color: palette.intransit.vertical_icon_color,
        custom_background_color: palette.intransit.background,
        footer_text_color: palette.intransit.footerText,
        footer_text: ProcessStep.IN_TRANSIT,
        footer_status_icon: (
          <InTransitIcon
            color={palette.primary.contrastText}
            backgroundColor={palette.intransit.footerText}
          />
        ),
      };
    case ARRIVED:
      return {
        header_text_color: palette.primary.contrastText,
        vertical_icon_color: palette.arrived.vertical_icon_color,
        custom_background_color: palette.arrived.background,
        footer_text_color: palette.arrived.footerText,
        footer_text: ProcessStep.ARRIVED,
        footer_status_icon: (
          <ArrivedIcon
            color={palette.primary.contrastText}
            backgroundColor={palette.arrived.footerText}
          />
        ),
      };
    case IN_PROCESS:
    case TRANSFERRED:
      return {
        header_text_color: palette.inprocess.header_text_color,
        vertical_icon_color: palette.inprocess.vertical_icon_color,
        custom_background_color: palette.inprocess.background,
        footer_text_color: palette.inprocess.footerText,
        footer_text:
          step === ProcessStep.IN_PROCESS
            ? ProcessStep.IN_PROCESS
            : ProcessStep.TRANSFERRED,
        footer_status_icon: (
          <InProcessIcon
            color={palette.primary.contrastText}
            backgroundColor={palette.inprocess.background}
          />
        ),
      };
    case DONE:
      return {
        header_text_color: palette.primary.contrastText,
        vertical_icon_color: palette.primary.contrastText,
        custom_background_color: palette.done.background,
        footer_text_color: palette.done.footerText,
        footer_text: ProcessStep.DONE,
        footer_status_icon: (
          <DoneIcon size="small" color={palette.done.background} />
        ),
      };
    case CANCELED:
      return {
        header_text_color: palette.primary.contrastText,
        vertical_icon_color: palette.canceled.vertical_icon_color,
        custom_background_color: palette.canceled.background,
        footer_text_color: palette.canceled.footerText,
        footer_text: ProcessStep.CANCELED,
        footer_status_icon: (
          <DeclinedIcon
            size="small"
            backgroundColor={palette.canceled.background}
          />
        ),
      };
    case DECLINED:
      return {
        header_text_color: palette.primary.contrastText,
        vertical_icon_color: palette.declined.vertical_icon_color,
        custom_background_color: palette.declined.background,
        footer_text_color: palette.declined.footerText,
        footer_text: ProcessStep.DECLINED,
        footer_status_icon: (
          <DeclinedIcon
            size="small"
            backgroundColor={palette.declined.background}
          />
        ),
      };
    case PAUSED:
      return {
        header_text_color: palette.primary.contrastText,
        vertical_icon_color: palette.declined.vertical_icon_color,
        custom_background_color: palette.declined.background,
        footer_text_color: palette.declined.footerText,
        footer_text: ProcessStep.PAUSED,
        footer_status_icon: (
          <PauseIcon size="small" color={palette.declined.footerText} />
        ),
      };
    case GENERATED:
    case PENDING_APPROVAL:
      return {
        header_text_color: palette.pending_approval.header_text_color,
        vertical_icon_color: palette.pending_approval.vertical_icon_color,
        custom_background_color: palette.pending_approval.background,
        footer_text_color: palette.pending_approval.footerText,
        footer_text: ProcessStep.PENDING_APPROVAL,
        footer_status_icon: (
          <PendingApprovalIcon
            size="small"
            color={palette.pending_approval.footerText}
          />
        ),
      };
    default:
      return {
        header_text_color: palette.pending_approval.header_text_color,
        vertical_icon_color: palette.pending_approval.vertical_icon_color,
        custom_background_color: palette.pending_approval.background,
        footer_text_color: palette.pending_approval.footerText,
        footer_text: step,
        footer_status_icon: (
          <PendingApprovalIcon
            size="small"
            color={palette.pending_approval.footerText}
          />
        ),
      };
  }
};

const getOrderTagIcon = (services = [], t, color) => {
  const orderTagIcons = {
    intake: {
      icon: <Garage style={{ color }} />,
      textService: t('detail.orderTag.intake'),
    },
    body_shop: {
      icon: <CarRepair style={{ color }} />,
      textService: t('detail.orderTag.body_shop'),
    },
    glass_chip: {
      icon: <Flare style={{ color }} />,
      textService: t('detail.orderTag.glass_chip'),
    },
    photos: {
      icon: <CameraAlt style={{ color }} />,
      textService: t('detail.orderTag.photos'),
    },
    wash_and_prep: {
      icon: <LocalCarWash style={{ color }} />,
      textService: t('detail.orderTag.wash_and_prep'),
    },
    detail: {
      icon: <AutoFixHigh style={{ color }} />,
      textService: t('detail.orderTag.detail'),
    },
    touch_up: {
      icon: <Brush style={{ color }} />,
      textService: t('detail.orderTag.touch_up'),
    },
    final_quality_check: {
      icon: <PublishedWithChanges style={{ color }} />,
      textService: t('detail.orderTag.final_quality_check'),
    },
    paint: {
      icon: <FormatPaint style={{ color }} />,
      textService: t('detail.orderTag.paint'),
    },
    buff: {
      icon: <AutoAwesome style={{ color }} />,
      textService: t('detail.orderTag.buff'),
    },
    wheel_repair: {
      icon: <Album style={{ color }} />,
      textService: t('detail.orderTag.wheel_repair'),
    },
    inspection: {
      icon: <ManageSearch style={{ color }} />,
      textService: t('detail.orderTag.inspection'),
    },
    paintless_dent_repair: {
      icon: <BlurCircular style={{ color }} />,
      textService: t('detail.orderTag.paintless_dent_repair'),
    },
    interior_repair: {
      icon: <EventSeat style={{ color }} />,
      textService: t('detail.orderTag.interior_repair'),
    },
    tint: {
      icon: <PhotoFilter style={{ color }} />,
      textService: t('detail.orderTag.tint'),
    },
    delivery: {
      icon: <Send style={{ color }} />,
      textService: t('detail.orderTag.delivery'),
    },
  };
  services = uniqueServicesOnly(services);
  if (services?.length > 1) {
    return {
      icon: <AutoAwesomeMotion style={{ color }} />,
      textService: formatTextServices(services),
    };
  }
  return orderTagIcons[services[0]];
};

const showCompletionDateOnDone = date => {
  return (
    <>
      <GenDate date={date} noTime />
    </>
  );
};

const generateServiceDate = (order, steps, currentStep) => {
  if (currentStep === DONE) {
    return showCompletionDateOnDone(order?.completedOn);
  } else if (currentStep === GENERATED || currentStep === ARRIVED) {
    let pendingDays = '' || 0;
    const date = currentStep === ARRIVED ? order?.arrivedOn : order?.date;
    pendingDays = differenceDateTime(date)?.days;
    return `${pendingDays} days ago`;
  } else if (currentStep === IN_TRANSIT) {
    const currentStepHistory = [...steps];
    const currentStepObj = currentStepHistory?.slice(-1)[0] || {};
    const createdAt = currentStepObj?.createdAt;
    const pendingDays = createdAt ? differenceDateTime(createdAt)?.days : 0;
    return `${pendingDays} days ago`;
  } else {
    const currentStepHistory = [...steps];
    const matchedStepIndex = currentStepHistory
      .reverse()
      .findIndex(item => item.step === currentStep);
    const currentStepObj = currentStepHistory[matchedStepIndex];
    const createdAt = currentStepObj?.createdAt;
    const pendingDays = createdAt ? differenceDateTime(createdAt)?.days : 0;
    return `${pendingDays} days ago`;
  }
};

const WOCardHeader = styled('div')`
  display: flex;
  gap: 0px;
  align-items: center;
  justify-content: space-between;
`;

const WOHeaderContent = styled(Box)`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const WOCardContent = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: 12px 11px;
  height: 130px;
  justify-content: space-between;
`;

const WOCardDescription = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 6px;
  height: 75px;
`;

const WONOtes = styled(Typography)`
  font-size: 12px;
  line-height: 15px;
  padding-left: 30px;
  max-height: 48px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledIcon = styled(Icon)`
  font-size: 16px;
  width: 20px;
  height: 20px;
`;

export default function WOTagCard({
  data,
  history,
  handleStartDone,
  addTechnician,
  hasActionsEnabled = true,
}) {
  const { t } = useTranslation('workOrdersTable');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const {
    step,
    orderType,
    services,
    woNumber,
    requiresApproval: pending_approval,
    vendorOrderId,
    amount: total_cost,
    notes,
    workflowHistory = [],
  } = data;

  const servicesWithAssignee = services
    ?.filter(service => !service.declinedAt)
    .filter(service => service.assignee);
  const stepIsArrived = step === ProcessStep.ARRIVED;
  const stepIsInProcess = step === ProcessStep.IN_PROCESS;
  const stepIsInDone = step === ProcessStep.DONE;
  const stepIsInCanceledOrDeclined =
    step === ProcessStep.CANCELED || step === ProcessStep.DECLINED;
  const enableStartLink = servicesWithAssignee?.length && stepIsArrived;
  const enableDoneLink = servicesWithAssignee?.length && stepIsInProcess;

  const {
    header_text_color,
    custom_background_color,
    footer_text_color,
    footer_text,
    footer_status_icon,
    vertical_icon_color,
  } = getStatusColors(step);

  const { icon, textService } = getOrderTagIcon(
    services,
    t,
    pending_approval ? '#0000008A' : footer_text_color
  );

  const handleClickOnWorkOrder = () =>
    history?.push({
      pathname: `/orders/${vendorOrderId}`,
    });

  const handleClickOnReconOrder = () => {
    if (!isReconditioning(data)) return;
    handleClickOnWorkOrder();
  };

  const handleClickOnNoReconOrder = () => {
    if (isReconditioning(data)) return;
    handleClickOnWorkOrder();
  };

  const handleMenuClick = action => {
    setAnchorEl(null);
    action();
  };

  return (
    <Card data-testid="wOTagCard">
      <Grid onClick={handleClickOnNoReconOrder}>
        <WOCardHeader
          sx={{
            height: '32px',
            padding: '4px 5px',
            backgroundColor: custom_background_color,
            borderRadius: '0',
          }}
        >
          <WOHeaderContent>
            <Box
              sx={{
                ':hover': {
                  cursor: 'pointer',
                },
              }}
            >
              {servicesWithAssignee.length > 0 ? (
                <Avatar
                  alt={[
                    servicesWithAssignee[0]?.assignee.firstName,
                    servicesWithAssignee[0]?.assignee.lastName,
                  ]
                    .filter(x => x)
                    .join(' ')}
                  src={servicesWithAssignee[0]?.assignee.photo}
                  sx={{ width: '24px', height: '24px' }}
                />
              ) : (
                <Tooltip title="Unassigned" arrow>
                  <Avatar
                    alt={[
                      servicesWithAssignee[0]?.assignee.firstName,
                      servicesWithAssignee[0]?.assignee.lastName,
                    ]
                      .filter(x => x)
                      .join(' ')}
                    src={servicesWithAssignee[0]?.assignee.photo}
                    sx={{ width: '24px', height: '24px' }}
                  />
                </Tooltip>
              )}
            </Box>
            <Typography
              variant="body2"
              fontWeight={'600'}
              color={header_text_color}
              title={`${t('detail.orderTag.workOrder')} ${woNumber}`}
              maxWidth={'151px'}
              noWrap
            >
              {getWOPrefix(orderType)} {getShortOrderNumber(woNumber)}
            </Typography>
          </WOHeaderContent>
          {hasActionsEnabled && (
            <>
              <IconButton
                sx={{ color: 'rgba(0, 0, 0, 0.54)', padding: 0 }}
                onClick={event => setAnchorEl(event.currentTarget)}
                id={data?.orderId}
                disabled={pending_approval || stepIsInCanceledOrDeclined}
              >
                <MoreVert sx={{ color: vertical_icon_color }} />
              </IconButton>
              <WOrderTagMenu
                anchorEl={anchorEl}
                open={open}
                setAnchorEl={setAnchorEl}
                data={data}
                handleMenuClick={handleMenuClick}
                addTechnician={addTechnician}
                handleStartDone={handleStartDone}
                stepIsInDone={stepIsInDone}
                stepIsInProcess={stepIsInProcess}
                pending_approval={pending_approval}
                enableStartLink={enableStartLink}
                enableDoneLink={enableDoneLink}
              />
            </>
          )}
        </WOCardHeader>
        <WOCardContent
          sx={{
            ':hover': {
              cursor: 'pointer',
            },
          }}
          onClick={handleClickOnReconOrder}
        >
          <WOCardDescription>
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '6px',
                justifyContent: 'space-between',
              }}
            >
              <Stack
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '6px',
                  height: '20px',
                }}
              >
                <Icon fontSize="small" sx={{ width: '24px', height: '24px' }}>
                  {icon}
                </Icon>
                <Typography
                  sx={{
                    fontSize: '12px',
                    lineHeight: '24px',
                    fontWeight: '600',
                  }}
                  color="#000"
                >
                  {textService}
                </Typography>
              </Stack>
              {orderType !== OrderTypes.RECONDITIONING && (
                <Typography variant="body1" color={'black'}>
                  <GenCurrency value={total_cost} />
                </Typography>
              )}
            </Stack>
            <WONOtes variant="body2" color="#6D6B6BCC">
              {notes}
            </WONOtes>
          </WOCardDescription>
          <div>
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'end',
              }}
            >
              <Stack
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'end',
                  gap: '4px',
                }}
              >
                <StyledIcon>{footer_status_icon}</StyledIcon>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '12px',
                    fontWeight: '600',
                    maxWidth: '107px',
                    lineHeight: '20px',
                  }}
                  color={footer_text_color}
                >
                  {footer_text}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{ fontSize: '10px', lineHeight: '22px' }}
                color={step === DONE ? footer_text_color : '#6D6B6BCC'}
              >
                {generateServiceDate(data, workflowHistory, step)}
              </Typography>
            </Stack>
          </div>
        </WOCardContent>
      </Grid>
    </Card>
  );
}
